import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '../services/AuthContext';
import { stripeAPI, authAPI } from '../services/api';

const StripeSuccess = () => {
  const [searchParams] = useSearchParams();
  const { user, updateUser } = useAuth();
  const [countdown, setCountdown] = useState(5);
  const [syncing, setSyncing] = useState(true);
  const [syncError, setSyncError] = useState('');

  const sessionId = searchParams.get('session_id');
  const isCoach = user?.role === 'coach';
  const redirectTo = isCoach ? '/coach/finances' : '/user/billing';

  useEffect(() => {
    const performSync = async () => {
      if (sessionId) {
        try {
          await stripeAPI.syncSession(sessionId);
          // Fetch updated user profile data and update context so it's not stale
          const profileRes = await authAPI.getMe();
          if (profileRes.data && profileRes.data.user) {
            updateUser(profileRes.data.user);
          }
        } catch (err) {
          console.error('Failed to sync Stripe checkout session:', err);
          setSyncError('Could not sync payment status automatically. Our servers will process it shortly.');
        } finally {
          setSyncing(false);
        }
      } else {
        setSyncing(false);
      }
    };

    performSync();
  }, [sessionId, updateUser]);

  useEffect(() => {
    if (syncing) return; // Wait until sync is finished to start redirect timer

    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          window.location.href = redirectTo;
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [redirectTo, syncing]);

  return (
    <div className="min-h-screen bg-[var(--bg-main)] flex items-center justify-center px-4 font-display">
      <div className="bg-[var(--surface-white)] rounded-3xl border border-[var(--border-light)] shadow-2xl shadow-black/10 p-10 max-w-md w-full text-center">

        {/* Animated success icon */}
        <div className="relative mx-auto mb-8 w-24 h-24">
          <div className="absolute inset-0 bg-green-500/10 rounded-full animate-ping" style={{ animationDuration: '2s' }} />
          <div className="relative w-24 h-24 bg-green-500 rounded-full flex items-center justify-center shadow-lg shadow-green-500/30">
            <svg className="w-12 h-12 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
          </div>
        </div>

        <h1 className="text-2xl font-bold text-[var(--text-primary)] mb-2">
          Payment Successful!
        </h1>
        <p className="text-[var(--text-secondary)] text-sm mb-6 leading-relaxed">
          {isCoach
            ? 'Your subscription is now active. You can start inviting clients to use your seats.'
            : 'Your subscription is now active. You can start chatting with your coach right away!'}
        </p>

        {sessionId && (
          <div className="bg-[var(--off-white)] rounded-2xl px-4 py-3 mb-6 border border-[var(--border-light)]">
            <p className="text-xs text-[var(--text-secondary)] font-mono break-all">
              Session: {sessionId.slice(0, 30)}...
            </p>
          </div>
        )}

        <div className="mb-6">
          {syncing ? (
            <div className="flex items-center justify-center gap-2 text-sm text-tangerine font-bold">
              <div className="w-5 h-5 border-2 border-tangerine/30 border-t-tangerine rounded-full animate-spin" />
              Activating subscription...
            </div>
          ) : syncError ? (
            <div className="text-xs text-orange-500 bg-orange-500/5 p-3 rounded-xl border border-orange-500/10">
              {syncError}
            </div>
          ) : (
            <div className="flex items-center justify-center gap-2 text-sm text-[var(--text-secondary)]">
              <div className="w-5 h-5 border-2 border-[var(--border-light)] border-t-tangerine rounded-full animate-spin" />
              Redirecting in {countdown}s...
            </div>
          )}
        </div>

        <Link
          to={redirectTo}
          className="inline-flex items-center gap-2 px-6 py-3 bg-tangerine text-white font-bold rounded-2xl shadow-md shadow-tangerine/20 hover:-translate-y-0.5 transition-all text-sm"
        >
          <span className="material-symbols-outlined text-lg">arrow_forward</span>
          Go now
        </Link>
      </div>
    </div>
  );
};

export default StripeSuccess;
